import { apiClient } from './apiClient';

export interface LoginResult {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

export interface RegisterResult {
  userSub: string;
  userConfirmed: boolean;
  email: string;
}

export interface RefreshTokensResult {
  accessToken: string;
  idToken: string;
  expiresIn: number;
  tokenType: string;
  /** Only when the Cognito app client rotates refresh tokens. */
  refreshToken?: string;
}

export async function confirmMfa(
  email: string,
  session: string,
  code: string,
): Promise<LoginResult> {
  const response = await apiClient<{ data: LoginResult }>(
    '/api/auth/login/mfa-confirm',
    {
      method: 'POST',
      body: { email, session, code },
    },
  );
  return response.data;
}

/** Optional e-mail consents at registration (both unticked by default) and the UI language. */
export interface RegisterEmailOptions {
  productUpdates?: boolean;
  taskReminders?: boolean;
  locale?: string;
}

export async function register(
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  privacyPolicyVersion: string,
  tosVersion: string,
  emailOptions?: RegisterEmailOptions,
): Promise<RegisterResult> {
  const response = await apiClient<{ data: RegisterResult }>(
    '/api/auth/signup',
    {
      method: 'POST',
      body: {
        email,
        password,
        firstName,
        lastName,
        privacyPolicyVersion,
        tosVersion,
        ...(emailOptions ?? {}),
      },
    },
  );
  return response.data;
}

export interface VerifyOtpResult {
  /**
   * Closed beta: 'pending' while an admin still has to approve the new account
   * (the app then shows /account-pending instead of the sign-in), 'approved'
   * when it can sign in right away. Older APIs send nothing → 'approved'.
   */
  approvalStatus: 'pending' | 'approved' | 'rejected';
}

export async function verifyOtp(
  email: string,
  code: string,
): Promise<VerifyOtpResult> {
  const res = await apiClient<{ data?: { approvalStatus?: unknown } } | undefined>(
    '/api/auth/signup/verify-otp',
    {
      method: 'POST',
      body: { email, code },
    },
  );
  const status = res?.data?.approvalStatus;
  return { approvalStatus: status === 'pending' || status === 'rejected' ? status : 'approved' };
}

export async function resendOtp(email: string): Promise<void> {
  await apiClient<{ data: unknown }>(
    '/api/auth/signup/resend-otp',
    {
      method: 'POST',
      body: { email },
    },
  );
}

export async function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  await apiClient<{ data: unknown }>(
    '/api/auth/confirm-forgot-password',
    {
      method: 'POST',
      body: { email, code, newPassword },
    },
  );
}

/**
 * POST /api/auth/refresh — new access/ID tokens for the refresh token.
 * `username` is the Cognito username (`cognito:username`); the backend needs
 * it for SECRET_HASH when the app client has a secret. A 401
 * (INVALID_REFRESH_TOKEN) means the refresh token is expired or revoked.
 * Use lib/sessionRefresh.refreshSession() to refresh the stored session.
 */
export async function refreshTokens(
  refreshToken: string,
  username?: string,
): Promise<RefreshTokensResult> {
  const response = await apiClient<{ data: RefreshTokensResult }>(
    '/api/auth/refresh',
    {
      method: 'POST',
      body: username ? { refreshToken, username } : { refreshToken },
    },
  );
  return response.data;
}
